import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Vacatures from "./pages/Vacatures";
import TabConfig from "./pages/TabConfig";
import AuditTrail from "./pages/AuditTrail";
import Gebruikersbeheer from "./pages/Gebruikersbeheer";
import AankomendeMails from "./pages/AankomendeMails";
import ErrorBoundary from "./components/ErrorBoundary";

// Loading spinner shown while auth state resolves
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-waves-blue border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-500 text-sm">Laden…</span>
      </div>
    </div>
  );
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: ("admin" | "kam" | "hmp" | "partner_admin")[];
}

function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function LoginRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Navigate to="/" replace />;

  return <Login />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/vacatures/:partnerId"
        element={
          <ProtectedRoute>
            <Layout>
              <Vacatures />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/tab-configuratie"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Layout>
              <TabConfig />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/audit-trail"
        element={
          <ProtectedRoute allowedRoles={["admin", "kam", "partner_admin"]}>
            <Layout>
              <AuditTrail />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/gebruikersbeheer"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Layout>
              <Gebruikersbeheer />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/aankomende-mails"
        element={
          <ProtectedRoute allowedRoles={["admin", "kam"]}>
            <Layout>
              <AankomendeMails />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ErrorBoundary>
  );
}
